import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import { useMemo } from 'react';
import { getVariableStartInfo } from './variableStart';
import { ETBundle, ETItem, ETLane, ETRuler, ETTrigger } from '../../../../components/ExperienceTimeline';
import { useUnsafeTranslation } from '../../../../i18next';
import { interpolateExperienceText } from '../../../../services/set/interpolation';
import {
  getAllMessageTemplates,
  getDirectEffectChildren,
  getMessageBatchesForTemplate,
  getTemplateDuration
} from '../../../../services/experiences/template';
import {
  getTimelinePositionForAutomationTemplate,
  getTimelinePositionForMessageTempate,
  getTimelinePositionForTaskTemplate
} from '../../../../services/experiences/timing';
import { ONE_DAY } from '../../../../services/time';
import { useTheme } from '../../../../themes';
import { getItemDisplayName } from '../../helpers/adminName';
import { AnchorField, useAllRelevantAnchorRulers } from '../../components/Anchors';
import { ActorDescriptor, useActorDescriptorsFromTemplate } from '../../components/Actors';

export const AUTOMATION_LANE_ID = 'AUTOMATION';

// Reuses every existing derivation helper (position/timing/trigger/batch
// logic) as-is — only the day-index conversion and the icon-language shape
// (ETItem/ETLane/ETBundle/ETTrigger/ETRuler) are new. No new queries, no
// new backend fields.
export const useExperienceTimelineModel = (
  tpl: Doc<IExperienceTemplate>,
  opts: {
    orgName: string;
    anchorFields: AnchorField[];
    showExpectedEndRuler: boolean;
  }
): {
  lanes: ETLane[];
  items: ETItem[];
  bundles: ETBundle[];
  triggers: ETTrigger[];
  rulers: ETRuler[];
  hasAutomation: boolean;
  actorDescriptors: { [key: string]: ActorDescriptor };
} => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const automationColor = theme.custom.colors.purple.w500;
  // A deactivated ("paused") item renders in flat grey-300 instead of its actor /
  // automation color — the process-timeline equivalent of the gantt's opacity:0.4.
  // Because the glyph, the task duration line, the end bar and every connector all
  // derive from `item.color`, setting it here greys the item and the line out of it
  // in one place.
  const deactivatedColor = theme.custom.colors.gray.w300;

  const actorDescriptors = useActorDescriptorsFromTemplate(tpl.data.supportingActors);
  const hasAutomation = tpl.data.automations.length > 0;

  const lanes = useMemo<ETLane[]>(() => {
    const base = Object.values(actorDescriptors).map((d) => ({
      id: d.key,
      label: d.label,
      color: d.color
    }));
    return hasAutomation
      ? [...base, { id: AUTOMATION_LANE_ID, label: t('Automation'), color: automationColor }]
      : base;
  }, [actorDescriptors, hasAutomation, automationColor, t]);

  const bundles = useMemo<ETBundle[]>(
    () =>
      getMessageBatchesForTemplate(tpl).map((b) => ({
        parentId: b.message.id,
        childIds: b.tasks.map((task) => task.id)
      })),
    [tpl]
  );

  const triggers = useMemo<ETTrigger[]>(() => {
    const directEffectChildren = getDirectEffectChildren(tpl);
    return Object.entries(directEffectChildren).flatMap(([fromId, toIds]) =>
      toIds.map((toId) => ({ fromId, toId }))
    );
  }, [tpl]);

  const items = useMemo<ETItem[]>(() => {
    const labelFor = (item: Parameters<typeof getItemDisplayName>[0]) =>
      interpolateExperienceText(
        getItemDisplayName(item),
        {
          experience: { vars: { title: tpl.data.title, tasks: {} } },
          org: { vars: { name: opts.orgName } },
          roles: []
        },
        { dateFormat: 'YYYY/MM/DD' }
      );

    const out: ETItem[] = [];

    getAllMessageTemplates(tpl.data.messages).forEach((m) => {
      const { start } = getTimelinePositionForMessageTempate(m.timing, tpl.data.tasks);
      const day = Math.round(start / ONE_DAY);
      const { variableStart, approximate } = getVariableStartInfo(m.timing);
      out.push({
        id: m.id,
        kind: 'MESSAGE',
        laneId: m.actor,
        startDay: day,
        endDay: day,
        label: labelFor(m),
        color: m.deactivated
          ? deactivatedColor
          : actorDescriptors[m.actor]?.color ?? theme.custom.colors.gray.w400,
        deactivated: !!m.deactivated,
        variableStart,
        approximate,
        meta: { type: 'MSG', item: m }
      });
    });

    tpl.data.tasks.forEach((task) => {
      const { start, end } = getTimelinePositionForTaskTemplate(task.timing, tpl.data.tasks);
      const startDay = Math.round(start / ONE_DAY);
      const endDay = Math.round(end / ONE_DAY);
      const { variableStart, approximate } = getVariableStartInfo(task.timing);
      out.push({
        id: task.id,
        kind: 'TASK',
        laneId: task.actor,
        startDay,
        endDay: Math.max(startDay, endDay),
        label: labelFor(task),
        color: task.deactivated
          ? deactivatedColor
          : actorDescriptors[task.actor]?.color ?? theme.custom.colors.gray.w400,
        deactivated: !!task.deactivated,
        variableStart,
        approximate,
        movable: task.timing.type === 'fixed',
        resizable: task.timing.type === 'fixed',
        meta: { type: 'TASK', item: task }
      });
    });

    tpl.data.automations.forEach((automation) => {
      const { start } = getTimelinePositionForAutomationTemplate(automation.timing, tpl.data.tasks);
      const day = Math.round(start / ONE_DAY);
      const { variableStart, approximate } = getVariableStartInfo(automation.timing);
      out.push({
        id: automation.id,
        kind: 'AUTOMATION',
        laneId: AUTOMATION_LANE_ID,
        startDay: day,
        endDay: day,
        label: labelFor(automation),
        color: automation.deactivated ? deactivatedColor : automationColor,
        deactivated: !!automation.deactivated,
        variableStart,
        approximate,
        movable: automation.timing.type === 'fixed',
        meta: { type: 'AUTOMATION', item: automation }
      });
    });

    return out;
  }, [tpl, actorDescriptors, automationColor, deactivatedColor, opts.orgName, theme]);

  const expectedEnd = useMemo(
    () =>
      items.length > 0
        ? Math.max(...items.map((i) => i.endDay * ONE_DAY)) + tpl.data.expectedEndThreshold
        : 0,
    [items, tpl.data.expectedEndThreshold]
  );

  const rawRulers = useAllRelevantAnchorRulers(
    tpl.data.anchor,
    opts.anchorFields,
    t,
    expectedEnd,
    opts.showExpectedEndRuler && items.length > 0
  );

  const rulers = useMemo<ETRuler[]>(
    () =>
      rawRulers.map((r) => ({
        day: Math.round(r.position / ONE_DAY),
        label: r.label,
        emphasis: r.colorMode === 'light' ? 'plain' : 'anchor'
      })),
    [rawRulers]
  );

  return { lanes, items, bundles, triggers, rulers, hasAutomation, actorDescriptors };
};

// Re-exported so call sites don't need a second import for a duration
// figure that's already computed identically today.
export { getTemplateDuration };
